// Shared constants + formatters for the Lead Tracker UI.
// Stage / outcome / source keys mirror backend services/lead_tracker.py.
import { getActiveCompanyTz, dayKeyInTz } from '@/lib/companyTime';

export const ALL = '__all__';
export const NONE = '__none__';

export const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'all', label: 'All time' },
];

export const STAGE_TONE = {
  new: 'bg-slate-100 text-slate-700 border-slate-300',
  reached_out: 'bg-sky-50 text-sky-700 border-sky-200',
  contacted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  booked: 'bg-amber-50 text-amber-800 border-amber-200',
  signed: 'bg-teal-50 text-teal-700 border-teal-200',
  won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost: 'bg-rose-50 text-rose-700 border-rose-200',
};

export const IRO_TONE = {
  within_30s: '#1F8A5B',
  within_5min: '#4C9A6A',
  within_10min: '#7FA05A',
  within_1h: '#B8A040',
  same_day: '#C89A3C',
  after_hours: '#B4763A',
  after_day_workday: '#A85440',
  after_day_sunday: '#8E4A55',
  no_time: '#8A8F99',
  unset: '#5C6370',
};

export const OUTCOME_GROUP_LABEL = {
  qualification: 'Lead qualification issue',
  no_connect: 'Never fully connected',
  after_estimate: 'Lost after estimate',
  different_direction: 'Went a different direction',
  postponed: 'Postponed — still live',
};

// Picking this reason keeps the lead OPEN and holds it out of the call queue
// until the date the office types. Mirrors POSTPONED_OUTCOME in the backend.
export const POSTPONED_OUTCOME = 'postponed';

// One-click disposal: a real lead the office is not going to pursue (below
// minimum, wrong service, out of range). Closes it as lost with a reason.
export const NOT_QUALIFIED_OUTCOME = 'not_qualified';

export const FUNNEL_STEPS = [
  { key: 'generated', label: 'Generated', color: '#4575A8' },
  { key: 'reached_out', label: 'Reached out', color: '#5C8AC2' },
  { key: 'connected', label: 'Connected', color: '#3F8F7A' },
  { key: 'booked', label: 'Booked', color: '#C89A3C' },
  { key: 'signed', label: 'Signed', color: '#2FA98A' },
  { key: 'won', label: 'Won', color: '#1F8A5B' },
];

export const pct = (v) => `${Math.round((Number(v) || 0) * 1000) / 10}%`;

export const money = (v, { compact = false } = {}) => {
  const n = Number(v) || 0;
  if (compact) {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`;
    if (abs >= 1_000) return `$${(n / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}k`;
  }
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export const num = (v) => (Number(v) || 0).toLocaleString();

// ── Follow-up helpers ────────────────────────────────────────────
export const FOLLOWUP_TONE = {
  overdue: 'bg-[#B4463A]/10 text-[#B4463A] border-[#B4463A]/30',
  due_today: 'bg-[#B4763A]/10 text-[#B4763A] border-[#B4763A]/30',
  cold: 'bg-[#4575A8]/10 text-[#4575A8] border-[#4575A8]/25',
  never_called: 'bg-[#8A8F99]/10 text-[#5C6370] border-[#8A8F99]/30',
  scheduled: 'bg-[#1F8A5B]/8 text-[#1F8A5B] border-[#1F8A5B]/25',
  parked: 'bg-[#8A8F99]/10 text-[#8A8F99] border-[#8A8F99]/25',
  paused: 'bg-[#8A8F99]/10 text-[#8A8F99] border-[#8A8F99]/25',
  none: 'bg-transparent text-[#C6C9CF] border-transparent',
};

export const FOLLOWUP_BUCKETS = [
  { key: '', label: 'All leads' },
  { key: 'needs_followup', label: 'Needs follow-up' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'cold', label: 'Cold' },
  { key: 'never_called', label: 'Never reached out' },
];

// The action row on the Lead Tracker — "what do I work next".
// The four follow-up groups are MUTUALLY EXCLUSIVE: they mirror the
// precedence in the backend's derive_followup(), so nothing is counted twice
// and a tile count always equals the number of rows showing that badge.
// `setup` is a different job (office steps on booked work) and filters on
// `setup=` rather than `followup=`.
export const ACTION_TILES = [
  {
    key: 'due_today', kind: 'followup', label: 'Call today', icon: 'phone',
    accent: '#C89A3C', tint: '#FFFBF0',
    why: 'Due today on your cadence', zero: 'Nothing due today',
  },
  {
    key: 'overdue', kind: 'followup', label: 'Past due', icon: 'alert',
    accent: '#B4463A', tint: '#FEF2F2',
    why: 'Missed their call date', zero: 'Nothing past due',
  },
  {
    key: 'never_called', kind: 'followup', label: 'Never reached out', icon: 'phoneoff',
    accent: '#5C6370', tint: '#F8FAFC',
    why: 'No call, text or email yet, not due yet', zero: 'Everyone has been reached out to',
  },
  {
    key: 'cold', kind: 'followup', label: 'Gone quiet', icon: 'snow',
    accent: '#4575A8', tint: '#F0F6FC',
    why: '', zero: 'No one has gone quiet',
  },
];

export const actionTileFor = (key) => ACTION_TILES.find((t) => t.key === key) || null;

// ── Booked consults pipeline ─────────────────────────────────────
// Booked leads leave the call queue and live here. The tracker only gives a
// heads-up on upcoming consults and asks "did it happen?" on the day — the
// CRM owns follow-up once the consult is done.
export const CONSULT_TONE = {
  today: 'bg-[#C89A3C]/12 text-[#8A6A1F] border-[#C89A3C]/35',
  upcoming: 'bg-[#4575A8]/10 text-[#3A5F86] border-[#4575A8]/25',
  awaiting: 'bg-[#B4763A]/12 text-[#8A551F] border-[#B4763A]/35',
  no_date: 'bg-[#8A8F99]/10 text-[#5C6370] border-[#8A8F99]/30',
  done: 'bg-[#1F8A5B]/10 text-[#1F7A50] border-[#1F8A5B]/30',
};

export const consultLabel = (c) => {
  if (!c) return '';
  switch (c.status) {
    case 'done': return 'Had consultation';
    case 'today': return 'Consult today';
    case 'upcoming': return `Consult ${relDays(c.appointment_date)}`;
    case 'awaiting': return `Was ${fmtDate(c.appointment_date)} — confirm`;
    case 'no_date': return 'Booked — set a date';
    default: return '';
  }
};

// One-tap "call me back in N days" chips (Log contact dialog + queue rows).
export const QUICK_SNOOZE = [2, 3, 5];

// Budget entered as a range in thousands — "70-90" reads as $70–90k.
export const budgetLabel = (lead) => {
  const lo = Number(lead?.budget_low) || 0;
  const hi = Number(lead?.budget_high) || 0;
  const k = (v) => (v >= 1000 ? `${Math.round(v / 1000)}` : `${v}`);
  if (lo && hi) return `$${k(lo)}–${k(hi)}k`;
  if (lo) return `$${k(lo)}k+`;
  if (hi) return `up to $${k(hi)}k`;
  return '';
};

// Is this a real lead? Junk still counts in the volume numbers (the ads were
// paid for) but drops out of the call-back queue. Mirrors QUALITY in
// backend services/lead_tracker.py.
export const QUALITY_LABELS = {
  real: 'Real lead',
  spam: 'Spam / junk',
  duplicate: 'Duplicate',
  not_a_lead: 'Not a lead',
};

export const QUALITY_SHORT = {
  spam: 'Spam',
  duplicate: 'Duplicate',
  not_a_lead: 'Not a lead',
};

export const QUALITY_WHY = {
  real: 'Counted everywhere and worked in the call queue.',
  spam: 'Junk form fill or bot. Still counted as a paid-for lead, never called again.',
  duplicate: 'Already in the tracker under another row. Still counted, never called again.',
  not_a_lead: 'Vendor, applicant, wrong number. Still counted, never called again.',
};

export const isJunk = (lead) => !!lead && !!lead.quality && lead.quality !== 'real';

export const CHECKLIST_TONE = {
  complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  requested: 'bg-amber-50 text-amber-800 border-amber-200',
  na: 'bg-slate-100 text-slate-500 border-slate-200',
  none: 'bg-white text-[#8A8F99] border-[#D5D8DD]',
};

export const FOLLOWUP_LABELS = {
  needs_followup: 'Needs a call',
  due_today: 'Due today',
  overdue: 'Overdue',
  cold: 'Cold',
  never_called: 'Never reached out',
  paused: 'Paused',
};

// `contact=` filter values understood by GET /lead-tracker/leads.
export const CONTACT_FILTER_LABELS = {
  reached_out: 'Reached out',
  connected: 'Connected',
  booked: 'Booked',
  no_contact: 'Never connected',
};

/** "today" / "3d ago" / "in 4d" from a date or datetime string. */
export const relDays = (s) => {
  if (!s) return '—';
  const raw = String(s);
  const d = new Date(raw.length <= 10 ? `${raw}T12:00:00` : raw);
  if (Number.isNaN(d.getTime())) return '—';
  const tz = getActiveCompanyTz();
  // A date-only value is already a calendar day; a datetime is resolved
  // to the company-zone day so "today"/"yesterday" match the office clock.
  const key = raw.length <= 10 ? raw : dayKeyInTz(d, tz);
  const todayKey = dayKeyInTz(new Date(), tz);
  const days = Math.round((Date.parse(`${key}T00:00:00Z`) - Date.parse(`${todayKey}T00:00:00Z`)) / 86400000);
  if (days === 0) return 'today';
  if (days === -1) return 'yesterday';
  if (days === 1) return 'tomorrow';
  return days < 0 ? `${-days}d ago` : `in ${days}d`;
};

// How a lead was last contacted — a short verb + relative time for the badge
// (e.g. "Texted 2d ago"). Mirrors OUTREACH_KINDS in backend lead_tracker.py.
export const TOUCH_KIND_VERB = {
  call: 'Called',
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  text: 'Texted',
  email: 'Emailed',
};

export const lastTouchLabel = (lead) => {
  const verb = TOUCH_KIND_VERB[lead?.last_touch_kind];
  if (!verb) return '';
  const when = lead?.last_touch_at ? relDays(lead.last_touch_at) : '';
  return when ? `${verb} ${when}` : verb;
};

export const followupLabel = (f) => {
  if (!f) return '';
  if (f.status === 'overdue') return `Overdue ${f.days_overdue}d`;
  if (f.status === 'due_today') return 'Due today';
  if (f.status === 'cold') return `Cold ${f.days_since_call}d`;
  if (f.status === 'never_called') return 'Never reached out';
  if (f.status === 'paused') return 'Paused';
  if (f.status === 'parked') return 'Parked';
  if (f.status === 'scheduled') return `Due ${relDays(f.due)}`;
  return '';
};

export const fmtDate = (s) => {
  if (!s) return '—';
  const dateOnly = String(s).length <= 10;
  const d = new Date(dateOnly ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return '—';
  // Date-only strings are calendar dates → no zone conversion (would
  // shift the day). Datetimes render in the company zone.
  return d.toLocaleDateString(undefined, {
    ...(dateOnly ? {} : { timeZone: getActiveCompanyTz() }),
    month: 'short', day: 'numeric', year: '2-digit',
  });
};

export const fmtDateTimeLocal = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const localToIso = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const duration = (secs) => {
  if (secs === null || secs === undefined) return '—';
  const s = Number(secs);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

// Response-speed median: "45s", "12m", then "Xh Ym" once it's an hour or more.
export const medianDuration = (secs) => {
  if (secs === null || secs === undefined) return '—';
  const s = Math.round(Number(secs));
  if (s < 60) return `${s}s`;
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export const labelOf = (list, key, fallback = '—') =>
  (list || []).find((o) => o.key === key)?.label || fallback;
